/**
 * Bundle selector
 *
 * Renders a set of radio-backed cards (Buy 1 pack, Buy 3 pack, ...). Selecting a
 * card marks it as active and, when `data-update-quantity` is present, writes the
 * card's quantity into the product form's quantity field for the same section. A
 * card that carries `data-variant-id` (price source "From variant") also puts that
 * variant into the form's hidden id field, so Add to cart adds the variant Shopify
 * prices it at.
 *
 * With `data-update-quantity` the sync also runs the other way: changing the quantity
 * selector selects the card with that quantity (or clears the selection when no card
 * matches), and the default card's quantity is applied when the page loads.
 */
const QUANTITY_UPDATE_EVENT = 'quantity-selector:update';

class BundleSelector extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#handleChange);

    // The form's id field is synced on load too, otherwise a pre-selected variant
    // card would be highlighted while Add to cart still adds the product's default.
    // Its quantity is applied as well, so the quantity selector starts in sync.
    const checked = this.querySelector('input[type="radio"]:checked');
    this.#select(checked, this.#syncsQuantity);

    if (this.#syncsQuantity) {
      this.#scope.addEventListener(QUANTITY_UPDATE_EVENT, this.#handleQuantityUpdate);
      this.#scope.addEventListener('change', this.#handleQuantityInput);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.#scope.removeEventListener(QUANTITY_UPDATE_EVENT, this.#handleQuantityUpdate);
    this.#scope.removeEventListener('change', this.#handleQuantityInput);
  }

  get #syncsQuantity() {
    return this.hasAttribute('data-update-quantity');
  }

  /** @returns {Element | Document} The section this selector and its product form live in. */
  get #scope() {
    return this.closest('.shopify-section') ?? document;
  }

  #handleChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    this.#select(input, true);
  };

  /** The quantity selector's +/- buttons. */
  #handleQuantityUpdate = (event) => {
    const quantity = Number(/** @type {CustomEvent} */ (event).detail?.quantity);
    if (Number.isFinite(quantity)) this.#selectByQuantity(quantity);
  };

  /** Typing a number into the quantity field. */
  #handleQuantityInput = (event) => {
    const field = event.target;
    if (!(field instanceof HTMLInputElement) || field.name !== 'quantity') return;
    this.#selectByQuantity(Number(field.value));
  };

  /**
   * Selects the card whose quantity matches, without writing back to the quantity
   * field. When no card matches, no card is selected.
   * @param {number} quantity
   */
  #selectByQuantity(quantity) {
    const inputs = /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('input[type="radio"]')]);
    const match = inputs.find((input) => Number(input.dataset.quantity) === quantity) ?? null;

    if (match) {
      if (!match.checked) {
        match.checked = true;
        this.#select(match, false);
      }
      return;
    }

    for (const input of inputs) input.checked = false;
    for (const option of this.querySelectorAll('.bundle-option')) {
      option.classList.remove('bundle-option--selected');
    }
  }

  /**
   * @param {HTMLInputElement | null} input - The checked radio input.
   * @param {boolean} propagate - Whether to push the quantity to the product form.
   */
  #select(input, propagate) {
    if (!input) return;

    for (const option of this.querySelectorAll('.bundle-option')) {
      option.classList.toggle('bundle-option--selected', option.contains(input));
    }

    if (propagate && this.#syncsQuantity) {
      this.#setQuantity(Number(input.dataset.quantity));
    }

    // A variant-backed card puts its variant into the form; picking a plain card
    // afterwards hands the form back the product's own variant.
    if (input.dataset.variantId) {
      this.#setVariant(input.dataset.variantId);
    } else if (this.#variantOverridden) {
      this.#setVariant(this.dataset.defaultVariantId);
    }
  }

  #variantOverridden = false;

  /** @param {string | undefined} variantId */
  #setVariant(variantId) {
    if (!variantId) return;

    const field = /** @type {HTMLInputElement | null} */ (
      this.#scope.querySelector('product-form-component input[name="id"]')
    );
    if (!field) return;

    this.#variantOverridden = variantId !== this.dataset.defaultVariantId;
    if (field.value === variantId) return;

    field.value = variantId;
    field.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** @param {number} quantity */
  #setQuantity(quantity) {
    if (!Number.isFinite(quantity) || quantity < 1) return;

    const field = /** @type {HTMLInputElement | null} */ (this.#scope.querySelector('input[name="quantity"]'));
    if (!field) return;

    const max = Number(field.max);
    const value = String(Number.isFinite(max) && max > 0 ? Math.min(quantity, max) : quantity);
    if (field.value === value) return;

    field.value = value;
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('bundle-selector')) {
  customElements.define('bundle-selector', BundleSelector);
}
